package nl.astraeus.wiki.view

import kotlinx.html.DIV
import kotlinx.html.js.onDragEndFunction
import kotlinx.html.js.onDragOverFunction
import kotlinx.html.js.onDragStartFunction
import kotlinx.html.js.onDropFunction
import nl.astraeus.wiki.service.UserService
import org.w3c.dom.DragEvent
import org.w3c.dom.HTMLElement

class DragAndDropState<T> {
  var draggingIndex: Int? = null
  var suppressClick: Boolean = false
  var draggedItem: T? = null
  var originalOrder: MutableList<T>? = null
  var lastHoverIndex: Int? = null
  var dropOccurred: Boolean = false

  fun reset() {
    draggingIndex = null
    suppressClick = false
    draggedItem = null
    originalOrder = null
    lastHoverIndex = null
    dropOccurred = false
  }
}

fun <T> DIV.dragAndDropReorder(
  index: Int,
  item: T,
  list: MutableList<T>,
  state: DragAndDropState<T>,
  enabled: Boolean,
  requestUpdate: () -> Unit,
  onCommit: ((List<T>) -> Unit)? = null,
) {
  if (!enabled) return

  attributes["draggable"] = "true"

  onDragStartFunction = { ev ->
    val e = ev.unsafeCast<DragEvent>()
    state.draggingIndex = index
    state.suppressClick = true
    state.dropOccurred = false
    state.draggedItem = item
    state.originalOrder = list.toMutableList()
    state.lastHoverIndex = index
    e.dataTransfer?.effectAllowed = "move"
    e.dataTransfer?.setData("text/plain", index.toString())
  }

  onDragOverFunction = { ev ->
    val e = ev.unsafeCast<DragEvent>()
    e.preventDefault()
    e.dataTransfer?.dropEffect = "move"

    if (UserService.loggedIn) {
      val dragged = state.draggedItem
      if (dragged != null) {
        val targetEl = e.currentTarget as? HTMLElement
        val rect = targetEl?.getBoundingClientRect()
        val pointerY = e.clientY
        val after = rect != null && pointerY > (rect.top + rect.height / 2.0)

        var desiredTarget = index + if (after) 1 else 0
        if (desiredTarget < 0) desiredTarget = 0
        if (desiredTarget > list.size) desiredTarget = list.size

        if (state.lastHoverIndex != desiredTarget) {
          val currentIndex = list.indexOfFirst { it === dragged }
          if (currentIndex >= 0) {
            if (desiredTarget == currentIndex || desiredTarget == currentIndex + 1) {
              state.lastHoverIndex = desiredTarget
            } else {
              list.removeAt(currentIndex)
              val adjustedInsert = if (desiredTarget > currentIndex) desiredTarget - 1 else desiredTarget
              val insertAt = adjustedInsert.coerceIn(0, list.size)
              list.add(insertAt, dragged)

              state.lastHoverIndex = desiredTarget
              requestUpdate()
            }
          }
        }
      }
    }
  }

  onDropFunction = { ev ->
    val e = ev.unsafeCast<DragEvent>()
    e.preventDefault()
    state.dropOccurred = true
    state.draggingIndex = null
    // keep suppressClick until drag end
  }

  onDragEndFunction = {
    if (!state.dropOccurred) {
      val snapshot = state.originalOrder
      if (snapshot != null) {
        list.clear()
        list.addAll(snapshot)
        requestUpdate()
      }
      state.reset()
    } else {
      // Successful drop → commit new order once
      val committed = list.toList()
      state.reset()
      onCommit?.invoke(committed)
    }
  }
}
